import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { useLocation, useParams, useReplacePath } from '../../../../contexts/RouterContext';
import { stripLastPathSegment } from '../../../../helpers/paths';
import useOnMount from '../../../../hooks/useOnMount';
import useToggle from '../../../../hooks/useToggle';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import projectGroupSelectors from '../../../../redux/project_grouping/selectors';
import { Icon, icons } from '../../../generic/Icon';
import ReleaseDisplay from '../../../projects/project_grouping/components/ReleaseDisplay';
import ReleaseGroupsReleaseSelector from '../../../projects/project_grouping/components/ReleaseSelector';
import useFetchIssuesAction from '../../IssuesInbox/hooks/useFetchIssuesAction';
import type { ProjectGroupRelease } from '../../../../types';

import StopComparing from './StopComparing';

import styles from '../IssueComparison.module.less';

const ReleaseGroupComparisonHeader: VoidFunctionComponent = () => {
  const dispatch = useDispatch();
  const replacePath = useReplacePath();
  const location = useLocation();

  // Get URL param data
  const params = useParams<{ projectGroupId: string; compareReleaseId?: string }>();

  // Get release group data from Redux using params
  const projectGroup = useSelector((state) => projectGroupSelectors.getCurrentProjectGroup(state, { params }));
  const selectedReleaseId = useSelector(projectGroupSelectors.getSelectedReleaseId);

  const setComparisonReleaseAndFetch = useFetchIssuesAction(IssuesV2Store.actions.setComparisonRelease);

  // Set up state for comparison release and dropdown toggle
  const [comparisonReleaseId, setComparisonReleaseId] = useState<number | undefined>(undefined);
  const [isDropdownOpen, toggleDropdown] = useToggle();

  // Track previous selectedReleaseId to detect changes
  const prevSelectedReleaseIdRef = useRef<number | undefined>(selectedReleaseId);

  useEffect(() => {
    if (selectedReleaseId && prevSelectedReleaseIdRef.current !== selectedReleaseId) {
      replacePath(`/projects/group/${params.projectGroupId}`);
    }
    prevSelectedReleaseIdRef.current = selectedReleaseId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when selectedReleaseId changes
  }, [selectedReleaseId]);

  // If we have a compareReleaseId in the URL params on mounting, load it into Redux store
  useOnMount(() => {
    if (params.compareReleaseId) {
      const releaseId = parseInt(params.compareReleaseId, 10);
      dispatch(IssuesV2Store.actions.setComparisonRelease(releaseId));
      setComparisonReleaseId(releaseId);
    }
  });

  useEffect(() => {
    if (!comparisonReleaseId) {
      return;
    }

    const newBasePath = `${params.compareReleaseId ? stripLastPathSegment(location.pathname) : location.pathname}`;

    replacePath(`${newBasePath}/${comparisonReleaseId}`, { search: location.search });
    setComparisonReleaseAndFetch(comparisonReleaseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when comparisonReleaseId changes
  }, [comparisonReleaseId]);

  // Find the selected release and comparison release
  const selectedRelease = projectGroup?.releases?.find((r: ProjectGroupRelease) => r.id === selectedReleaseId);
  const comparisonRelease = projectGroup?.releases?.find((r: ProjectGroupRelease) => r.id === comparisonReleaseId);

  const handleReleaseSelect = (releaseId: number) => {
    setComparisonReleaseId(releaseId);
    toggleDropdown();
  };

  return (
    <header className={styles.headerCard}>
      <div className="container container-responsive">
        <div className={styles.headerTitleSection}>
          <span className={styles.title}>Comparing Changes</span>
          <StopComparing />
        </div>
        Compare:{' '}
        <div className={styles.revisionComparisonContainer}>
          <div className={styles.revision}>
            <ReleaseDisplay release={selectedRelease} placeholder="Current Release" variant="light" />
          </div>
          <Icon icon={icons.arrowLeft} />
          <div className={styles.revision}>
            <div className={styles.releaseSelectorWrapper}>
              <div
                role="button"
                tabIndex={0}
                onClick={toggleDropdown}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDropdown();
                  }
                }}
                className={styles.releaseDisplayButtonContainer}
              >
                <ReleaseDisplay
                  release={comparisonRelease}
                  placeholder="Select a release to compare"
                  shouldShowChevron
                  variant="light"
                />
              </div>
              {isDropdownOpen && (
                <ReleaseGroupsReleaseSelector
                  projectGroupId={parseInt(params.projectGroupId, 10)}
                  selectedReleaseId={comparisonReleaseId}
                  onReleaseSelect={handleReleaseSelect}
                  onClose={toggleDropdown}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default ReleaseGroupComparisonHeader;
